/* eslint-disable @typescript-eslint/no-explicit-any */
import Cookies from 'js-cookie';
import { submitDynamicForm } from '../helper/helper';

export type LspRedirectProps = {
  payload: Record<string, unknown>;
  endpoint: string;
};

export type Ppv5LspBackRedirectInput = {
  ppv5LoyaltyBackBtnData?: string;
  ppv5LoyaltyWelcomeData?: string;
  mrEnrolled?: string | null;
};

const queryParam = (query: Record<string, any>, key: string): string => {
  const value = query?.[key];
  if (Array.isArray(value)) {
    return value[0] ?? '';
  }
  return value ?? '';
};

const isSafeRedirectUrl = (url: string): boolean => {
  try {
    const { protocol } = new URL(url);
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
};

export const isRedirectToLspQuery = (query: Record<string, any>): boolean => query?.redirectToLSP === 'true';

export const isBonvoyBackToLsp = (query: Record<string, any>): boolean =>
  query?.ppv5Enrollment === 'true' && query?.redirectToLSP === 'true';

export const decodeLSPCookieInfo = (value?: string): Record<string, unknown> | null => {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(Buffer.from(value, 'base64').toString('utf8'));
  } catch {
    return null;
  }
};

export const isGuestConnectedUpgrade = (cookieDetails: Record<string, unknown> | null, upgradeUrl: string): boolean => {
  if (!cookieDetails) {
    return false;
  }

  return (
    cookieDetails['ACTION'] === 'CONNECTED' &&
    cookieDetails['Z'] === 'GUEST' &&
    upgradeUrl !== '' &&
    (cookieDetails['TIERS'] === 'FP' || cookieDetails['TIERS'] === 'PP')
  );
};

export const buildPpv5LspBackRedirectProps = (
  query: Record<string, any>,
  input: Ppv5LspBackRedirectInput = {}
): LspRedirectProps | null => {
  if (!isRedirectToLspQuery(query)) {
    return null;
  }

  const ppv5LoyaltyBackBtnData = input.ppv5LoyaltyBackBtnData;
  if (!ppv5LoyaltyBackBtnData) {
    return null;
  }

  const ppv5LoyaltyBackBtnDataURLDetails = decodeLSPCookieInfo(ppv5LoyaltyBackBtnData);
  if (!ppv5LoyaltyBackBtnDataURLDetails) {
    return null;
  }

  const ppv5LoyaltyWelcomeDataURLDetails = decodeLSPCookieInfo(input.ppv5LoyaltyWelcomeData ?? '');
  const upgradeUrl = queryParam(query, 'U_URL');
  const loginUrl = queryParam(query, 'L_URL');
  const mrEnrolled = input.mrEnrolled === 'true';

  const isUpgradeNextURL = isGuestConnectedUpgrade(ppv5LoyaltyBackBtnDataURLDetails, upgradeUrl);
  const isUpgradeNextConnectedURL = isGuestConnectedUpgrade(ppv5LoyaltyWelcomeDataURLDetails, upgradeUrl);

  const endpoint = mrEnrolled
    ? isUpgradeNextConnectedURL
      ? upgradeUrl
      : loginUrl
    : isUpgradeNextURL
    ? upgradeUrl
    : loginUrl;

  if (!endpoint) {
    return null;
  }

  const submitFormEndpoint = endpoint.indexOf('://') === -1 ? `https://${endpoint}` : endpoint;
  if (!isSafeRedirectUrl(submitFormEndpoint)) {
    return null;
  }
  const payload = mrEnrolled
    ? { ...(ppv5LoyaltyWelcomeDataURLDetails ?? {}), APS: 'N' }
    : { ...ppv5LoyaltyBackBtnDataURLDetails };

  return { payload, endpoint: submitFormEndpoint };
};

/**
 * POST-redirects back to LSP without rendering page content. Returns true when a form submit was triggered.
 */
export const executePpv5LspBackRedirect = (query: Record<string, any>): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const redirectProps = buildPpv5LspBackRedirectProps(query, {
    ppv5LoyaltyBackBtnData: Cookies.get('PPv5LoyaltyBackBtnData'),
    ppv5LoyaltyWelcomeData: Cookies.get('PPv5LoyaltyWelcomeData') ?? '',
    mrEnrolled: sessionStorage.getItem('mr_enrolled'),
  });

  if (!redirectProps) {
    return false;
  }

  submitDynamicForm(redirectProps.payload, redirectProps.endpoint);
  return true;
};

export const resolveLspEndpoint = (
  query: Record<string, any>,
  payload: Record<string, unknown> | null
): string | null => {
  if (!payload) {
    return null;
  }

  const isUpgrade = payload?.ACTION?.toString()?.toUpperCase() === 'UPGRADE';
  const endpoint = isUpgrade ? query?.U_URL : query?.L_URL;

  if (!endpoint || typeof endpoint !== 'string') {
    return null;
  }

  const resolvedEndpoint = endpoint.indexOf('://') === -1 ? `https://${endpoint}` : endpoint;
  return isSafeRedirectUrl(resolvedEndpoint) ? resolvedEndpoint : null;
};

export const buildLspRedirectFromContext = (context: {
  query: Record<string, any>;
  req?: { cookies?: Record<string, string> };
}): LspRedirectProps | null => {
  if (!isBonvoyBackToLsp(context.query)) {
    return null;
  }

  const payload = decodeLSPCookieInfo(context.req?.cookies?.PPv5NextURLData);
  if (!payload) {
    return null;
  }

  const endpoint = resolveLspEndpoint(context.query, payload);
  if (!endpoint) {
    return null;
  }

  return { payload, endpoint };
};
